import { getTHREE } from './gameContext.js';
import { findBoardCentreMarker, translatePosition } from './centreBoardMarker.js';
import { boardFunctions } from './boardFunctions.js';
import { isBattleRegionCell, getActiveBattle } from './battle/battleRules.js';

/**
 * Pull the camera back to a high-level overview of the entire board.
 *
 * Computes the bounding box of all populated cells and frames it so
 * the whole island is visible. Falls back to a sensible default extent
 * when the board is empty.
 *
 * Battle arenas live on a remote grid; framing them together with the
 * organic world would zoom the camera into orbit. So the fit uses only
 * the region the local player is actually in: their arena while
 * battling, the organic world otherwise (wherever that cluster sits —
 * it is not required to hug the origin).
 *
 * @returns {boolean} True if at least one in-view cell was framed.
 */
export function setCameraToOverview(camera, controls, gameState) {
	if (!camera || !controls) return false;

	const board = gameState?.board;
	const battle = getActiveBattle(gameState);
	const ARENA_FRAME_RADIUS = 40;
	const includeCell = (x, z) => {
		if (battle && battle.centre) {
			return Math.abs(x - battle.centre.x) <= ARENA_FRAME_RADIUS
				&& Math.abs(z - battle.centre.z) <= ARENA_FRAME_RADIUS;
		}
		return !isBattleRegionCell(x, z);
	};

	let centerX = 0, centerZ = 0, maxExtent = 50, counted = 0;
	if (board && board.cells) {
		let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
		for (const key of Object.keys(board.cells)) {
			const [x, z] = key.split(',').map(Number);
			if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
			if (!includeCell(x, z)) continue;
			if (x < minX) minX = x;
			if (x > maxX) maxX = x;
			if (z < minZ) minZ = z;
			if (z > maxZ) maxZ = z;
			counted++;
		}
		if (counted > 0) {
			centerX = (minX + maxX) / 2;
			centerZ = (minZ + maxZ) / 2;
			maxExtent = Math.max(maxX - minX, maxZ - minZ, 50);
		}
	}

	if (counted === 0) return false;

	const viewDistance = Math.max(60, maxExtent * 0.8);
	camera.position.set(
		centerX + viewDistance * 0.5,
		viewDistance * 0.7,
		centerZ + viewDistance * 0.5,
	);
	if (controls.target) controls.target.set(centerX, 0, centerZ);
	camera.lookAt(centerX, 0, centerZ);
	if (controls.update) controls.update();
	return true;
}

const CAMERA_DEFAULTS = {
	FOV: 50,
	// Keep in sync with the camera built in `enhanced-gameCore.initGame`
	// — a near plane of 1 (not 0.1) is what keeps the depth buffer
	// precise enough on 16-bit mobile depth buffers.
	NEAR: 1,
	FAR: 1000,
	MIN_DISTANCE: 8,
	MAX_DISTANCE: 80,
	DAMPING_FACTOR: 0.12,
	FLY_DURATION_MS: 1800,
	/** Longest sweep (distance-scaled) — cross-world drone flights. */
	FLY_DURATION_MAX_MS: 4500,
	/** Cap on how high the flight arc climbs above the endpoints. */
	FLY_ARC_MAX_HEIGHT: 120,
	/**
	 * Beyond this horizontal distance a fly becomes a WARP (fade out,
	 * teleport, fade in). Battle arenas sit ~2,000+ cells from the
	 * world — flying that far is seconds of empty sea and sky, so the
	 * screen "just goes blue". Ordinary in-world hops stay flights.
	 */
	WARP_DISTANCE: 300,
	WARP_FADE_IN_MS: 350,
	WARP_FADE_OUT_MS: 650,
	KING_VIEW_DISTANCE: 16,
	/**
	 * Aim this many cells ahead of the king, and this high, instead of
	 * at its feet. Tetrominoes spawn a few cells in front of the king
	 * at height 10 (tetromino/spawn.js); aimed at the feet, the camera
	 * pitched down so far that a newcomer's first piece sat above the
	 * top edge of the screen while its fall waits for their first key.
	 */
	KING_VIEW_LOOK_AHEAD: 4,
	KING_VIEW_LOOK_HEIGHT: 3,
	/** Furthest the home view backs off on a tall, narrow screen. */
	KING_VIEW_PORTRAIT_MAX_SCALE: 1.5,
	FALLBACK_POSITION: { x: 10, y: 25, z: 10 },
	FALLBACK_TARGET: { x: 0, y: 0, z: 0 }
};

let activeFlyAnimation = null;

/**
 * Set up camera with proper position and controls
 */
export function setupCamera(camera, controls, renderer) {
	const THREE = getTHREE();
	if (!camera) {
		console.error('Cannot setup camera: camera is undefined');
		return null;
	}

	camera.position.set(
		CAMERA_DEFAULTS.FALLBACK_POSITION.x,
		CAMERA_DEFAULTS.FALLBACK_POSITION.y,
		CAMERA_DEFAULTS.FALLBACK_POSITION.z
	);
	camera.lookAt(
		CAMERA_DEFAULTS.FALLBACK_TARGET.x,
		CAMERA_DEFAULTS.FALLBACK_TARGET.y,
		CAMERA_DEFAULTS.FALLBACK_TARGET.z
	);

	if (typeof THREE.OrbitControls !== 'undefined' && renderer) {
		if (!controls) {
			controls = new THREE.OrbitControls(camera, renderer.domElement);
		}

		controls.enableDamping = true;
		controls.dampingFactor = CAMERA_DEFAULTS.DAMPING_FACTOR;
		controls.screenSpacePanning = true;
		controls.minDistance = CAMERA_DEFAULTS.MIN_DISTANCE;
		controls.maxDistance = CAMERA_DEFAULTS.MAX_DISTANCE;
		controls.maxPolarAngle = Math.PI / 2 - 0.05;
		controls.target.set(
			CAMERA_DEFAULTS.FALLBACK_TARGET.x,
			CAMERA_DEFAULTS.FALLBACK_TARGET.y,
			CAMERA_DEFAULTS.FALLBACK_TARGET.z
		);

		controls.touches = {
			ONE: THREE.TOUCH.ROTATE,
			TWO: THREE.TOUCH.DOLLY_PAN
		};

		controls.rotateSpeed = 0.7;
		controls.panSpeed = 0.8;
		controls.zoomSpeed = 1.0;
		controls.update();

		return controls;
	}

	console.warn('THREE.OrbitControls not available, camera will be static');
	return null;
}

/**
 * Reset camera for gameplay — finds the player's king and flies to it.
 * Retries automatically if chess pieces haven't loaded yet.
 */
export function resetCameraForGameplay(renderer, camera, controls, gameState, scene, animate = true, forceImmediate = false, onComplete) {
	const THREE = getTHREE();
	if (!camera) {
		console.error('Cannot reset camera: camera is undefined');
		return;
	}

	if (!controls && typeof THREE.OrbitControls !== 'undefined' && renderer) {
		controls = new THREE.OrbitControls(camera, renderer.domElement);
		controls.enableDamping = true;
		controls.dampingFactor = CAMERA_DEFAULTS.DAMPING_FACTOR;
		controls.minDistance = CAMERA_DEFAULTS.MIN_DISTANCE;
		controls.maxDistance = CAMERA_DEFAULTS.MAX_DISTANCE;
	}

	if (!controls) {
		camera.position.set(
			CAMERA_DEFAULTS.FALLBACK_POSITION.x,
			CAMERA_DEFAULTS.FALLBACK_POSITION.y,
			CAMERA_DEFAULTS.FALLBACK_POSITION.z
		);
		camera.lookAt(
			CAMERA_DEFAULTS.FALLBACK_TARGET.x,
			CAMERA_DEFAULTS.FALLBACK_TARGET.y,
			CAMERA_DEFAULTS.FALLBACK_TARGET.z
		);
		if (typeof onComplete === 'function') onComplete();
		return;
	}

	const attemptFly = (retriesLeft) => {
		const success = moveToPlayerZone(camera, controls, gameState, renderer, scene, animate, forceImmediate, onComplete);
		if (!success && retriesLeft > 0) {
			setTimeout(() => attemptFly(retriesLeft - 1), 500);
		} else if (!success && typeof onComplete === 'function') {
			onComplete();
		}
	};

	attemptFly(6);
}

/**
 * Move the camera to view a player's home zone.
 *
 * Falls back to the centre of the player's home zone if their king
 * is missing from `gameState.chessPieces` (e.g. just captured) so the
 * caller still gets a useful camera move instead of a silent no-op.
 *
 * @returns {boolean} True if a camera move was issued.
 */
export function moveToPlayerZone(camera, controls, gameState, renderer, scene, animate = true, forceImmediate = false, onComplete, targetPlayerId) {
	const playerId = targetPlayerId || gameState.localPlayerId;
	if (!playerId) return false;
	if (!camera || !controls) return false;

	let boardPosition = null;
	let orientation = 0;

	const kingPiece = boardFunctions.getPlayersKing(gameState, playerId, false);
	if (kingPiece) {
		boardPosition = kingPiece.position;
		orientation = Number.isFinite(kingPiece.orientation) ? kingPiece.orientation : 0;
	} else {
		const zone = gameState.homeZones && gameState.homeZones[playerId];
		if (zone && Number.isFinite(zone.x) && Number.isFinite(zone.z)) {
			boardPosition = {
				x: zone.x + (zone.width || 8) / 2,
				z: zone.z + (zone.height || 2) / 2,
			};
			orientation = Number.isFinite(zone.orientation) ? zone.orientation : 0;
		}
	}

	if (!boardPosition) {
		console.warn('moveToPlayerZone: no king or home zone for player', playerId);
		return false;
	}

	const position = translatePosition(boardPosition, gameState, true);
	if (!position) return false;

	// Portrait phones see only a sliver sideways, so the spawned piece
	// (up to a few cells left/right of the king) fell outside the view.
	// Back off on narrow screens; landscape keeps the standard distance.
	const aspect = Number.isFinite(camera.aspect) && camera.aspect > 0 ? camera.aspect : 1;
	const portraitPullBack = Math.min(CAMERA_DEFAULTS.KING_VIEW_PORTRAIT_MAX_SCALE, Math.max(1, 0.75 / aspect));
	const dist = CAMERA_DEFAULTS.KING_VIEW_DISTANCE * portraitPullBack;
	const height = dist * Math.sin(Math.PI / 4);

	// The king's forward direction; the camera sits behind it.
	let forwardX = 0;
	let forwardZ = 1;
	switch (orientation) {
		case 1: forwardX = 1; forwardZ = 0; break;
		case 2: forwardZ = -1; break;
		case 3: forwardX = -1; forwardZ = 0; break;
		default: break;
	}

	// On our own home view, pan sideways halfway toward a piece that is
	// still waiting to fall, so the king and the piece both fit (spawns
	// can sit several cells to the king's side).
	let panX = 0;
	let panZ = 0;
	const tetromino = playerId === gameState.localPlayerId ? gameState.currentTetromino : null;
	if (tetromino?.position && Array.isArray(tetromino.shape) && tetromino.heightAboveBoard > 0) {
		const centre = translatePosition({
			x: tetromino.position.x + ((tetromino.shape[0]?.length || 1) - 1) / 2,
			z: tetromino.position.z + (tetromino.shape.length - 1) / 2,
		}, gameState, true);
		if (centre) {
			// Right of the king's facing, as in tetromino/spawn.js.
			const rightX = -forwardZ;
			const rightZ = forwardX;
			const lateral = (centre.x - position.x) * rightX + (centre.z - position.z) * rightZ;
			panX = rightX * lateral / 2;
			panZ = rightZ * lateral / 2;
		}
	}

	const targetPosition = {
		x: position.x - forwardX * dist + panX,
		y: height,
		z: position.z - forwardZ * dist + panZ
	};
	const ahead = CAMERA_DEFAULTS.KING_VIEW_LOOK_AHEAD;
	const targetLookAt = {
		x: position.x + forwardX * ahead + panX,
		y: CAMERA_DEFAULTS.KING_VIEW_LOOK_HEIGHT,
		z: position.z + forwardZ * ahead + panZ
	};

	if (animate && !forceImmediate) {
		flyToPosition(camera, controls, targetPosition, targetLookAt, renderer, scene, onComplete);
	} else {
		cancelFlyAnimation();
		controls.target.set(targetLookAt.x, targetLookAt.y, targetLookAt.z);
		camera.position.set(targetPosition.x, targetPosition.y, targetPosition.z);
		controls.update();
		if (renderer && scene) renderer.render(scene, camera);
		if (typeof onComplete === 'function') onComplete();
	}
	return true;
}

/**
 * Called once the first piece has appeared after a home-view flight.
 * If any of its blocks fall outside the view, fly the home view again;
 * now that the piece exists, `moveToPlayerZone` pans toward it.
 *
 * @returns {boolean} True if a camera move was issued.
 */
export function bringPendingTetrominoIntoView(camera, controls, gameState, renderer, scene) {
	const group = gameState?.currentTetrominoShapeGroup;
	if (!camera || !controls || !group?.children?.length) return false;
	const THREE = getTHREE();
	camera.updateMatrixWorld();
	const point = new THREE.Vector3();
	const margin = 0.9;
	const inView = group.children.every((block) => {
		block.getWorldPosition(point);
		point.project(camera);
		return point.z < 1 && Math.abs(point.x) <= margin && Math.abs(point.y) <= margin;
	});
	if (inView) return false;
	return moveToPlayerZone(camera, controls, gameState, renderer, scene, true);
}

/**
 * Cancel any running fly animation
 */
let activeFlyControlsRestore = null;
function cancelFlyAnimation() {
	if (activeFlyAnimation !== null) {
		cancelAnimationFrame(activeFlyAnimation);
		activeFlyAnimation = null;
	}
	if (typeof activeFlyControlsRestore === 'function') {
		activeFlyControlsRestore();
		activeFlyControlsRestore = null;
	}
}

/**
 * Smooth cubic ease-out
 */
function easeOutCubic(t) {
	return 1 - Math.pow(1 - t, 3);
}

// ── Warp transition (fade → teleport → fade) ───────────────────────────────

let warpTimer = null;

/** Full-screen sky-coloured fade layer, created lazily and reused. */
function ensureWarpOverlay() {
	let overlay = document.getElementById('camera-warp-overlay');
	if (!overlay) {
		overlay = document.createElement('div');
		overlay.id = 'camera-warp-overlay';
		overlay.style.cssText = [
			'position:fixed', 'inset:0',
			// Matches the scene fog / sky so the fade reads as "lens
			// whiting out over the sea", not a UI curtain.
			'background:#C5F0FF',
			'opacity:0', 'pointer-events:none', 'z-index:9000',
			'transition:opacity 350ms ease',
		].join(';');
		document.body.appendChild(overlay);
	}
	return overlay;
}

/**
 * Long-haul camera move: fade the screen to sky, snap the camera at
 * the destination, fade back in. Replaces multi-second flights over
 * empty water (world ↔ battle arenas) with a ~1s transition.
 */
function warpToPosition(camera, controls, targetPosition, targetLookAt, renderer, scene, onComplete) {
	cancelFlyAnimation();
	if (warpTimer) {
		clearTimeout(warpTimer);
		warpTimer = null;
	}

	const overlay = ensureWarpOverlay();
	overlay.style.transitionDuration = `${CAMERA_DEFAULTS.WARP_FADE_IN_MS}ms`;
	// Force a style flush so the transition runs even if the overlay
	// was created this frame.
	void overlay.offsetWidth;
	overlay.style.opacity = '1';

	warpTimer = setTimeout(() => {
		warpTimer = null;
		controls.target.set(targetLookAt.x, targetLookAt.y, targetLookAt.z);
		camera.position.set(targetPosition.x, targetPosition.y, targetPosition.z);
		controls.update();
		if (renderer && scene) {
			try { renderer.render(scene, camera); } catch (_e) { /* main loop covers it */ }
		}
		// Give the destination one painted frame before lifting the veil.
		requestAnimationFrame(() => {
			overlay.style.transitionDuration = `${CAMERA_DEFAULTS.WARP_FADE_OUT_MS}ms`;
			overlay.style.opacity = '0';
			if (typeof onComplete === 'function') onComplete();
		});
	}, CAMERA_DEFAULTS.WARP_FADE_IN_MS + 50);
}

/**
 * Animate camera with a smooth flying arc to target position.
 *
 * OrbitControls damping is disabled for the duration of the fly so the
 * controls don't fight our per-frame position writes. The previous
 * damping state is restored when the fly finishes (or is cancelled).
 */
export function flyToPosition(camera, controls, targetPosition, targetLookAt, renderer, scene, onComplete) {
	cancelFlyAnimation();

	const startPosition = {
		x: camera.position.x,
		y: camera.position.y,
		z: camera.position.z
	};
	const startLookAt = controls.target.clone();

	// Snapshot and disable damping/auto-rotate while we drive the camera
	// directly; otherwise OrbitControls smooths our writes a second time
	// and the camera "stutters" or drifts past the target.
	const dampingWas = controls.enableDamping === true;
	const autoRotateWas = controls.autoRotate === true;
	controls.enableDamping = false;
	controls.autoRotate = false;

	const restoreControls = () => {
		controls.enableDamping = dampingWas;
		controls.autoRotate = autoRotateWas;
	};
	activeFlyControlsRestore = restoreControls;

	// Sweep duration and arc height both scale with distance so short
	// hops stay snappy. TRULY long hauls (world ↔ battle arena,
	// ~2,800 units of empty sea) warp instead: fade out, teleport,
	// fade in — flying that far just looks like a blue screen.
	const dx = targetPosition.x - startPosition.x;
	const dz = targetPosition.z - startPosition.z;
	const horizontalDist = Math.sqrt(dx * dx + dz * dz);
	if (horizontalDist > CAMERA_DEFAULTS.WARP_DISTANCE && typeof document !== 'undefined') {
		restoreControls();
		activeFlyControlsRestore = null;
		warpToPosition(camera, controls, targetPosition, targetLookAt, renderer, scene, onComplete);
		return;
	}
	const duration = Math.min(
		CAMERA_DEFAULTS.FLY_DURATION_MS + horizontalDist * 8,
		CAMERA_DEFAULTS.FLY_DURATION_MAX_MS
	);
	const startTime = performance.now();
	const arcHeight = Math.min(horizontalDist * 0.4, CAMERA_DEFAULTS.FLY_ARC_MAX_HEIGHT);

	const midY = Math.max(startPosition.y, targetPosition.y) + arcHeight;

	function tick(now) {
		const elapsed = now - startTime;
		const rawProgress = Math.min(elapsed / duration, 1);
		const t = easeOutCubic(rawProgress);

		// Quadratic Bezier for Y (creates smooth arc), linear lerp for X/Z
		const oneMinusT = 1 - t;
		const arcY = oneMinusT * oneMinusT * startPosition.y +
			2 * oneMinusT * t * midY +
			t * t * targetPosition.y;

		camera.position.x = startPosition.x + (targetPosition.x - startPosition.x) * t;
		camera.position.y = arcY;
		camera.position.z = startPosition.z + (targetPosition.z - startPosition.z) * t;

		controls.target.x = startLookAt.x + (targetLookAt.x - startLookAt.x) * t;
		controls.target.y = startLookAt.y + (targetLookAt.y - startLookAt.y) * t;
		controls.target.z = startLookAt.z + (targetLookAt.z - startLookAt.z) * t;

		controls.update();

		if (renderer && scene) {
			try { renderer.render(scene, camera); } catch (_) { /* main loop will pick it up */ }
		}

		if (rawProgress < 1) {
			activeFlyAnimation = requestAnimationFrame(tick);
		} else {
			activeFlyAnimation = null;
			activeFlyControlsRestore = null;
			camera.position.set(targetPosition.x, targetPosition.y, targetPosition.z);
			controls.target.set(targetLookAt.x, targetLookAt.y, targetLookAt.z);
			restoreControls();
			controls.update();
			if (typeof onComplete === 'function') onComplete();
		}
	}

	activeFlyAnimation = requestAnimationFrame(tick);
}

/**
 * Generic camera animation (linear lerp with ease-out)
 */
export function animateCamera(camera, controls, targetPosition, lookAt, renderer, scene) {
	flyToPosition(camera, controls, targetPosition, lookAt, renderer, scene);
}
